import filter from 'lodash/filter'
import isEmpty from 'lodash/isEmpty'
import { getAccountIdentifier } from 'v2/pages/settings/integrations/utils/account-identifier'
import type { AppVariables, SecretVariable } from 'v2/shared/services/external-apps-service'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import { logger } from 'v2/utils/logger'

export const CUSTOM_SECRETS_GROUP = 'Custom Secrets'

export interface SecretVariableGroup {
  variables: SecretVariable[]
  connectedApp: RExternalOrgApp | null
}

interface BuildSecretVariableGroupsInput {
  appVariables: AppVariables | undefined
  connectedApps: RExternalOrgApp[] | undefined
  customSecrets: SecretVariable[] | undefined
  searchTerm: string
}

const matchesSearch = (variable: SecretVariable, term: string) => {
  const needle = term.toLowerCase()
  return (
    variable?.name?.toLowerCase().includes(needle) ||
    variable?.display_name?.toLowerCase().includes(needle) ||
    variable?.description?.toLowerCase().includes(needle)
  )
}

/**
 * The picker's accordion groups: "Custom Secrets" first, then one per connected app.
 *
 * The two sources are independent: app variables may still be loading, or have
 * failed, and that must not hide the custom secrets that did load.
 */
export const buildSecretVariableGroups = ({
  appVariables,
  connectedApps,
  customSecrets,
  searchTerm,
}: BuildSecretVariableGroupsInput): Record<string, SecretVariableGroup> => {
  const groups: Record<string, SecretVariableGroup> = {}

  // Listed first: it is the group most pickers are opened for, and the app groups can run to dozens.
  if (Array.isArray(customSecrets) && customSecrets.length > 0) {
    const matching = filter(customSecrets, (variable: SecretVariable) =>
      matchesSearch(variable, searchTerm),
    )
    if (!isEmpty(matching)) {
      groups[CUSTOM_SECRETS_GROUP] = { variables: matching, connectedApp: null }
    }
  }

  if (appVariables && connectedApps) {
    for (const [appUuid, variables] of Object.entries(appVariables)) {
      if (!Array.isArray(variables)) {
        logger.error(`Variables for app ${appUuid} is not an array`, variables)
        continue
      }
      const connectedApp = connectedApps.find((app) => app.id === appUuid)
      if (!connectedApp) {
        logger.warn(`Connected app not found for UUID: ${appUuid}`)
        continue
      }
      const matching = filter(variables, (variable: SecretVariable) =>
        matchesSearch(variable, searchTerm),
      )
      if (isEmpty(matching)) continue

      // Several connections to one provider share a name; the account identifier tells them apart.
      const accountInfo = getAccountIdentifier(connectedApp)
      const appKey = accountInfo ? `${connectedApp.name} (${accountInfo})` : connectedApp.name
      groups[appKey] = { variables: matching, connectedApp }
    }
  }

  return groups
}
